/**
 * API utilities for Avida Personal Care Platform
 * Sprint 2: Added document management APIs
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('avida_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('avida_token');
      localStorage.removeItem('avida_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Health API
export const healthAPI = {
  check: () => api.get('/health'),
};

// User Management API
export const usersAPI = {
  list: (params = {}) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  updateRole: (id, role, reason) => api.put(`/users/${id}/role`, { role, reason }),
  archive: (id, reason) => api.put(`/users/${id}/archive`, { reason }),
};

// Audit Logs API
export const auditAPI = {
  list: (params = {}) => api.get('/audit-logs', { params }),
  get: (id) => api.get(`/audit-logs/${id}`),
  exportCsv: (params = {}) => api.get('/audit-logs/export/csv', { params, responseType: 'blob' }),
};

// Document Types API (Sprint 2)
export const documentTypesAPI = {
  list: (params = {}) => api.get('/document-types', { params }),
  get: (code) => api.get(`/document-types/${code}`),
  create: (data) => api.post('/document-types', data),
  update: (code, data) => api.put(`/document-types/${code}`, data),
};

// Documents API (Sprint 2)
export const documentsAPI = {
  list: (params = {}) => api.get('/documents', { params }),
  get: (id) => api.get(`/documents/${id}`),
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getVersions: (typeCode, entityType, entityId) => 
    api.get(`/documents/versions/${typeCode}/${entityType}/${entityId}`),
  archive: (id, reason) => api.put(`/documents/${id}/archive`, { reason }),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  completeness: (entityType, entityId) => api.get(`/documents/completeness/${entityType}/${entityId}`),
};

// Clients API (Sprint 3)
export const clientsAPI = {
  list: (params = {}) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  changeStatus: (id, data) => api.post(`/clients/${id}/status`, data),
  archive: (id, reason) => api.put(`/clients/${id}/archive`, { reason }),
  checkActivationGate: (id) => api.get(`/clients/${id}/activation-gate`),
  getStatusHistory: (id) => api.get(`/clients/${id}/status-history`),
};

// Visits API (Sprint 4)
export const visitsAPI = {
  list: (params = {}) => api.get('/visits', { params }),
  get: (id) => api.get(`/visits/${id}`),
  create: (data) => api.post('/visits', data),
  update: (id, data) => api.put(`/visits/${id}`, data),
  createOrUpdateNote: (id, data) => api.put(`/visits/${id}/note`, data),
  complete: (id) => api.post(`/visits/${id}/complete`),
  markBillable: (id) => api.post(`/visits/${id}/billable`),
  checkBillable: (id) => api.get(`/visits/${id}/billable-check`),
  archive: (id, reason) => api.put(`/visits/${id}/archive`, { reason }),
};

// Diagnostics API
export const diagnosticsAPI = {
  get: () => api.get('/diagnostics'),
  getErrors: (limit = 20) => api.get(`/diagnostics/errors?limit=${limit}`),
  getRoles: () => api.get('/diagnostics/roles'),
};

// Personnel API (Sprint 6)
export const personnelAPI = {
  list: (params = {}) => api.get('/personnel', { params }),
  get: (id) => api.get(`/personnel/${id}`),
  create: (data) => api.post('/personnel', data),
  update: (id, data) => api.put(`/personnel/${id}`, data),
  archive: (id, reason) => api.put(`/personnel/${id}/archive`, { reason }),
  getCompliance: (id, asOfDate) => api.get(`/personnel/${id}/compliance`, { params: { as_of_date: asOfDate } }),
  getTrainings: (id) => api.get(`/personnel/${id}/trainings`),
  addTraining: (id, data) => api.post(`/personnel/${id}/trainings`, data),
  verifyTraining: (personnelId, trainingId) => api.post(`/personnel/${personnelId}/trainings/${trainingId}/verify`),
};

// Training API (Sprint 6)
export const trainingAPI = {
  getRequirements: (params = {}) => api.get('/training-requirements', { params }),
  getMatrix: (params = {}) => api.get('/training-matrix', { params }),
  exportMatrixCsv: (params = {}) => api.get('/training-matrix/export', { params, responseType: 'blob' }),
};

// Survey API (Sprint 7)
export const surveyAPI = {
  getDashboard: () => api.get('/survey/dashboard'),
  exportClientPack: (clientId, dateFrom, dateTo) => {
    const formData = new FormData();
    formData.append('client_id', clientId);
    if (dateFrom) formData.append('date_from', dateFrom);
    if (dateTo) formData.append('date_to', dateTo);
    return api.post('/survey/export/client', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    });
  },
  exportAgencyPack: (dateFrom, dateTo) => {
    const formData = new FormData();
    if (dateFrom) formData.append('date_from', dateFrom);
    if (dateTo) formData.append('date_to', dateTo);
    return api.post('/survey/export/agency', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob'
    });
  },
};

// Sandata Import API (Sprint 9)
export const sandataAPI = {
  // Import endpoints
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('exceptions_file', file);
    return api.post('/sandata/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  preview: (sessionId, columnMap) => api.post('/sandata/import/preview', {
    session_id: sessionId,
    column_map_exceptions: columnMap
  }),
  commit: (sessionId, columnMap) => api.post('/sandata/import/commit', {
    session_id: sessionId,
    column_map_exceptions: columnMap
  }),
  getImportRuns: (params = {}) => api.get('/sandata/import/runs', { params }),
  getImportRun: (id) => api.get(`/sandata/import/runs/${id}`),
  
  // Mapping endpoints
  getMappings: (params = {}) => api.get('/sandata/mappings', { params }),
  createMapping: (data) => api.post('/sandata/mappings', data),
  updateMapping: (id, data) => api.put(`/sandata/mappings/${id}`, data),
  archiveMapping: (id, reason) => api.post(`/sandata/mappings/${id}/archive`, { reason }),
  
  // Column map presets
  getColumnMaps: (fileType) => api.get('/sandata/column-maps', { params: { file_type: fileType } }),
  createColumnMap: (data) => api.post('/sandata/column-maps', data),
};

// EVV Exception API (Sprint 5 + Sprint 9 enhancements)
export const evvAPI = {
  list: (params = {}) => api.get('/evv-exceptions', { params }),
  get: (id) => api.get(`/evv-exceptions/${id}`),
  create: (data) => api.post('/evv-exceptions', data),
  update: (id, data) => api.put(`/evv-exceptions/${id}`, data),
  changeStatus: (id, data) => api.post(`/evv-exceptions/${id}/status`, data),
  archive: (id, reason) => api.put(`/evv-exceptions/${id}/archive`, { reason }),
  getHistory: (id) => api.get(`/evv-exceptions/${id}/history`),
  getAgingReport: () => api.get('/evv-exceptions/aging-report'),
  linkVisit: (id, visitId) => api.post(`/evv-exceptions/${id}/link-visit`, { visit_id: visitId }),
  searchVisits: (id, params = {}) => api.get(`/evv-exceptions/${id}/visit-search`, { params }),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/evv-exceptions/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export default api;
